import type { Dispatch, SetStateAction } from 'react';
import type { QueryClient } from '@tanstack/react-query';
import {
  mergePlatformChatMessagesIntoCache,
  patchPlatformChatSessionPreview,
  seedPlatformChatMessageAttachmentsFromDrafts,
} from '../platformChatMessagesCache';
import { chatQueryKeys } from '../chatQueryKeys';
import { platformChatService } from '../../../services/PlatformChatService';
import type { ChatMessage } from '../../../types/chatMessage';
import type { ChatMessageSendOptions } from '../../../types/chatMessagesContext';
import {
  clearOptimisticSessionMessages,
  hasMessageId,
  sessionHasPersistedMessages,
} from './chatMessagesProviderHelpers';

type RunPlatformChatSendTurnParams = {
  queryClient: QueryClient;
  sessionId: string;
  content: string;
  options: ChatMessageSendOptions | undefined;
  userId: string | undefined;
  userName: string | undefined;
  youBadgeLabel: string;
  assistantName: string;
  setOptimisticMessagesBySessionId: Dispatch<
    SetStateAction<Record<string, ChatMessage[]>>
  >;
  beginSending: (sessionId: string) => void;
  endSending: (sessionId: string) => void;
};

export async function runPlatformChatSendTurn({
  queryClient,
  sessionId,
  content,
  options,
  userId,
  userName,
  youBadgeLabel,
  assistantName,
  setOptimisticMessagesBySessionId,
  beginSending,
  endSending,
}: RunPlatformChatSendTurnParams): Promise<void> {
  const trimmedContent = content.trim();
  const attachmentIds = options?.attachmentIds ?? [];
  if (trimmedContent === '' && attachmentIds.length === 0) {
    return;
  }

  const now = new Date().toISOString();
  const optimisticUserMessageId = `local-user-${Date.now()}`;
  const optimisticAssistantMessageId = `local-assistant-${Date.now()}`;
  const optimisticUserMessage: ChatMessage = {
    id: optimisticUserMessageId,
    sessionId,
    content: trimmedContent,
    sentAt: now,
    imageFormat: options?.imageFormat,
    imageAspectRatio: options?.imageAspectRatio,
    author: {
      type: 'user',
      id: userId ?? '',
      name: userName ?? youBadgeLabel,
    },
  };
  const optimisticAssistantMessage: ChatMessage = {
    id: optimisticAssistantMessageId,
    sessionId,
    content: '',
    sentAt: now,
    author: {
      type: 'ai',
      name: assistantName,
    },
    isPendingAssistantReply: true,
    isStreamingAssistantReply: false,
    ...(options?.imageGenerationEnabled
      ? {
          imageFormat: options.imageFormat,
          imageAspectRatio: options.imageAspectRatio,
        }
      : {}),
  };

  const isFirstMessageTurn = !sessionHasPersistedMessages(
    queryClient,
    sessionId,
  );

  beginSending(sessionId);
  setOptimisticMessagesBySessionId((current) => ({
    ...current,
    [sessionId]: [optimisticUserMessage, optimisticAssistantMessage],
  }));

  try {
    const turn = await platformChatService.streamAssistantReply(
      sessionId,
      trimmedContent,
      {
        onFirstChunk: () => {
          setOptimisticMessagesBySessionId((current) => {
            const sessionMessages = current[sessionId] ?? [];
            return {
              ...current,
              [sessionId]: sessionMessages.map((message) =>
                message.id === optimisticAssistantMessageId
                  ? {
                      ...message,
                      isPendingAssistantReply: false,
                      isStreamingAssistantReply: true,
                    }
                  : message,
              ),
            };
          });
        },
        onChunk: (chunk) => {
          setOptimisticMessagesBySessionId((current) => {
            const sessionMessages = current[sessionId] ?? [];
            return {
              ...current,
              [sessionId]: sessionMessages.map((message) => {
                if (message.id !== optimisticAssistantMessageId) {
                  return message;
                }
                const nextContent = chunk.startsWith(message.content)
                  ? chunk
                  : `${message.content}${chunk}`;
                return {
                  ...message,
                  content: nextContent,
                  isPendingAssistantReply: false,
                  isStreamingAssistantReply: true,
                };
              }),
            };
          });
        },
      },
      {
        webSearchEnabled: options?.webSearchEnabled,
        imageGenerationEnabled: options?.imageGenerationEnabled,
        imageFormat: options?.imageFormat,
        imageAspectRatio: options?.imageAspectRatio,
        attachmentIds,
        maxResults: options?.maxResults,
        minScore: options?.minScore,
      },
    );
    const streamedMessages = [turn.userMessage, turn.assistantMessage].filter(
      hasMessageId,
    );
    if (hasMessageId(turn.userMessage) && turn.userMessage.hasAttachment) {
      seedPlatformChatMessageAttachmentsFromDrafts(
        queryClient,
        sessionId,
        turn.userMessage.id,
      );
    }
    const didMergeStreamedMessages = mergePlatformChatMessagesIntoCache(
      queryClient,
      sessionId,
      streamedMessages,
    );
    clearOptimisticSessionMessages(setOptimisticMessagesBySessionId, sessionId);
    if (didMergeStreamedMessages) {
      queryClient.setQueryData(
        chatQueryKeys.sessionDraftAttachments(sessionId),
        [],
      );
    }
    const previewMessage =
      (hasMessageId(turn.assistantMessage) ? turn.assistantMessage : null) ??
      (hasMessageId(turn.userMessage) ? turn.userMessage : null);
    if (previewMessage) {
      patchPlatformChatSessionPreview(queryClient, sessionId, previewMessage);
    }
    // Backend assigns an AI title after the first answered turn.
    if (isFirstMessageTurn) {
      await queryClient.refetchQueries({
        queryKey: chatQueryKeys.sessions(),
      });
    }
  } catch (error) {
    clearOptimisticSessionMessages(setOptimisticMessagesBySessionId, sessionId);
    throw error;
  } finally {
    endSending(sessionId);
  }
}
